// =============================================================================
// ui/state/usePnlUnit.ts — the display unit, and the only place it is written.
// -----------------------------------------------------------------------------
// Same shape as `useCustomWidgets` and `useLeakRules`, against the same column,
// for the same reason.
//
// ── 🚨 THE GATE, WHICH IS NOT OPTIONAL ──
//
// `usePreferences.update()` stays callable while a read has FAILED, because a
// patch normally carries only the field being changed and so cannot revert what
// it never saw. `app_flags` is the exception: the patch IS the whole blob. After
// a failed read `prefs` is null, the spread in `withPnlUnit` would start from
// `{}`, and saving a unit would wipe the leak rules, the custom widgets, every
// layout choice and the planned-target unit.
//
// So `canWrite` is checked before the write, and a refusal says so in words
// rather than failing quietly.
//
// ── ⚠ THE BLOB IS RE-READ INSIDE THE CALLBACK ──
//
// Not closed over from render. Two changes without a reload between them would
// otherwise both spread the same starting blob, and the second would erase the
// first — which for this control means switching Points → Ticks could drop a
// widget added a moment earlier on another tab. `prefs` is in the dependency
// list on purpose.
//
// ── ⚠ READING NEVER FAILS; ONLY WRITING DOES ──
//
// `readPnlUnit` falls back to dollars for a null blob, so a screen always has a
// unit to render even mid-load. The asymmetry is deliberate: a wrong-looking
// unit for one frame is recoverable, and a wiped flags blob is not.
// =============================================================================

import { useCallback, useMemo, useState } from 'react';
import { usePreferences } from './preferences';
import { readPnlUnit, withPnlUnit, type PnlUnit } from '../../selectors/pnlUnit';
import { reportFailure } from '../../io/diagnostics/errorLog';

export interface PnlUnitState {
  /** The unit every P&L on screen is in. `usd` until preferences load. */
  unit: PnlUnit;
  /** False while preferences are loading or unreadable — no write may proceed. */
  canWrite: boolean;
  busy: boolean;
  error: string | null;
  /** Returns the failure message, or null on success. */
  setUnit: (next: PnlUnit) => Promise<string | null>;
}

const NOT_READY = 'Your settings haven’t loaded, so the unit can’t be changed yet — '
  + 'saving now would overwrite the settings this screen never read. Reload and try again.';

export function usePnlUnit(): PnlUnitState {
  const { phase, prefs, update } = usePreferences();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canWrite = phase === 'ready' && prefs !== null;
  const unit = useMemo(() => readPnlUnit(prefs?.appFlags), [prefs]);

  const setUnit = useCallback(async (next: PnlUnit): Promise<string | null> => {
    if (!canWrite || !prefs) { setError(NOT_READY); return NOT_READY; }
    // ⚠ Read the blob HERE, not at render. See the header.
    const flags = withPnlUnit(prefs.appFlags, next);
    setBusy(true);
    setError(null);
    try {
      // ⚠ ONE KEY IN THE PATCH. A second would be a column this control never
      // read, which is the rule that stops a screen reverting a field it does
      // not show.
      await update({ appFlags: flags });
      return null;
    } catch (e) {
      const msg = reportFailure('save display unit', e, 'The unit could not be saved.');
      setError(msg);
      return msg;
    } finally {
      setBusy(false);
    }
  }, [canWrite, prefs, update]);

  return { unit, canWrite, busy, error, setUnit };
}
